import React from 'react';
import { motion } from 'framer-motion';

interface DataPoint {
  month: number;
  score: number;
}

interface ProgressGraphProps {
  data: DataPoint[];
  color: string;
  labelColor: string;
  months: string[];
}

export const ProgressGraph: React.FC<ProgressGraphProps> = ({ 
  data, 
  color, 
  labelColor,
  months 
}) => {
  const getY = (score: number) => {
    return 100 - score;
  };

  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = getY(d.score);
    return `${x}%,${y}%`;
  }).join(' ');

  return (
    <div className="w-full h-[400px]">
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
        {/* Y-axis grid lines and labels */}
        {Array.from({ length: 11 }, (_, i) => i * 10).map((value) => (
          <React.Fragment key={value}>
            <line
              x1="0"
              y1={`${100 - value}%`}
              x2="100%"
              y2={`${100 - value}%`}
              stroke={labelColor}
              strokeWidth="0.2"
              opacity="0.2"
            />
            <text
              x="-5"
              y={`${100 - value}%`}
              dominantBaseline="middle"
              className="font-['SF_Pro_Display'] text-[0.1rem] font-[300] tracking-[-0.05em] fill-[#292B2D]"
            >
              {value}%
            </text>
          </React.Fragment>
        ))}

        {/* Progress line */}
        <motion.polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />

        {/* Data points */}
        {data.map((d, i) => (
          <motion.circle
            key={i}
            cx={`${(i / (data.length - 1)) * 100}%`}
            cy={`${getY(d.score)}%`}
            r="1.5"
            fill={color}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.1 }}
          />
        ))}

        {/* X-axis labels */}
        {months.map((month, i) => (
          <text
            key={i}
            x={`${(i / (months.length - 1)) * 100}%`}
            y="105%"
            textAnchor="middle"
            className="font-['SF_Pro_Display'] text-[0.1rem] font-[300] tracking-[-0.05em] fill-[#292B2D]"
          >
            {month}
          </text>
        ))}
      </svg>
    </div>
  );
};